import React, { useCallback, useEffect } from "react";

import cn from "classnames";
import { startOfDay, startOfMonth } from "date-fns/esm";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import RoundButton from "@app/component/RoundButton";
import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import NavigationButtons from "@app/scene/activity/component/ActivityCalendarPage/components/CalendarToolbar/NavigationButtons";
import { getTimeframeStartForDate } from "@app/scene/activity/utils/navigation";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { getWeekStart } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import CollapsedCalendar from "./CollapsedCalendar";
import ExpandedCalendar from "./ExpandedCalendar";
import styles from "./MiniCalendar.module.scss";

interface Props {
  className?: string;
}

const MiniCalendar: React.FC<Props> = ({ className }) => {
  const intl = useIntl();
  const { updateViewStateAndFetch, viewState } = useCalendarView<Activity>();
  // We're pretty sure startAt is always a part of the filter
  // @ts-ignore
  let startAtDate = parseApiDate(viewState.filter.startAt.value);
  startAtDate = (
    viewState.viewMode === CalendarViewMode.MONTH
      ? startOfMonth
      : viewState.viewMode === CalendarViewMode.WEEK
      ? getWeekStart
      : startOfDay
  )(startAtDate);

  const [collapsed, collapse, expand, toggle] = useBoolean();

  const sideCalendarAnalytics = useExtendedAnalytics(
    "Calendar above Map",
    smartPlannerViewAnalytics
  );

  useEffect(() => {
    if (viewState.viewMode === CalendarViewMode.MONTH) {
      expand();
    } else {
      collapse();
    }
  }, [expand, collapse, viewState.viewMode]);

  const handleClick = useCallback(
    (day: Date) => {
      const newStartAt = getTimeframeStartForDate(viewState.viewMode, day);
      updateViewStateAndFetch({
        filter: {
          ...viewState.filter,
          startAt: { operator: FilterOperator.ON, value: newStartAt.toISOString() },
        },
      });
    },
    [updateViewStateAndFetch, viewState.filter, viewState.viewMode]
  );

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.header}>
        <div className={styles.title}>{formatDate(startAtDate, "LLLL yyyy")}</div>
        <div>
          <NavigationButtons
            analyticIssuer={sideCalendarAnalytics}
            forceViewMode={
              !collapsed || viewState.viewMode === CalendarViewMode.MONTH
                ? CalendarViewMode.MONTH
                : CalendarViewMode.WEEK
            }
            timeframeStart={startAtDate}
          />
        </div>
      </div>
      {collapsed ? (
        <CollapsedCalendar onClick={handleClick} timeframeStart={startAtDate} />
      ) : (
        <ExpandedCalendar onClick={handleClick} timeframeStart={startAtDate} />
      )}
      <RoundButton
        className={cn(styles.expandCollapse, { [styles.expanded]: !collapsed })}
        hasMenu
        onClick={toggle}
      >
        {intl.formatMessage(
          {
            id: "activities.calendarView.sidebar.calendar.expandCollapse",
            defaultMessage: "{collapsed, select, true {Expand} other {Collapse}}",
            description:
              "Expand/Collapse button labels for mini-calendar in sidebar in Activity Calendar view",
          },
          { collapsed }
        )}
      </RoundButton>
    </div>
  );
};

export default MiniCalendar;
